import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Play, Clock, Shield, AlertTriangle, HardHat, Siren, ChevronRight, X } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { previewModules } from '../data/mock';

const iconMap = {
  Shield: Shield,
  AlertTriangle: AlertTriangle,
  HardHat: HardHat,
  Siren: Siren
};

const PreviewSection = ({ onModuleClick }) => {
  const [activePreview, setActivePreview] = useState(null);
  const [showVideo, setShowVideo] = useState(false);

  return (
    <section className="py-20 bg-slate-50">
      <div className="container mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Interactive Preview</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
            Experience the Course
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Get a taste of what you&apos;ll learn with free preview content
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Video preview card with real image */}
          <Card 
            className="relative overflow-hidden rounded-2xl aspect-video flex items-center justify-center group cursor-pointer hover:shadow-2xl transition-all duration-500"
            onClick={() => setShowVideo(true)}
          >
            {/* Background image */}
            <img 
              src="https://images.unsplash.com/photo-1662309376159-b95fb193d96b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHx3b3JrcGxhY2UlMjBzYWZldHklMjB0cmFpbmluZ3xlbnwwfHx8fDE3Njg2OTAyNjJ8MA&ixlib=rb-4.1.0&q=85&w=800"
              alt="Safety equipment and training"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-slate-900/20 group-hover:from-slate-900/70 transition-all" />
            
            <div className="relative z-10 text-center">
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:bg-emerald-500/80 transition-all duration-300 border border-white/30">
                <Play className="w-8 h-8 text-white ml-1" />
              </div>
              <h3 className="text-white font-semibold text-lg">Course Introduction</h3>
              <p className="text-slate-200 text-sm flex items-center justify-center gap-1 mt-1">
                <Clock className="w-4 h-4" /> 5 min preview
              </p>
            </div>

            {/* Badge */}
            <div className="absolute bottom-4 right-4">
              <span className="text-xs text-white bg-emerald-500 px-3 py-1 rounded-full font-medium">
                Free Preview
              </span>
            </div>
          </Card>

          {/* Module list */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <ChevronRight className="w-5 h-5 text-emerald-500" />
              Explore Modules
            </h3>
            
            {previewModules.map((module) => {
              const IconComponent = iconMap[module.icon] || Shield;
              return (
                <Card
                  key={module.id}
                  className={`p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 border-2 ${
                    activePreview === module.id 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-transparent hover:border-emerald-200'
                  }`}
                  onClick={() => {
                    setActivePreview(module.id);
                    onModuleClick && onModuleClick(module);
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-slate-900 mb-1">{module.title}</h4>
                      <p className="text-sm text-slate-600 line-clamp-2">{module.description}</p>
                      <span className="text-xs text-emerald-600 font-medium mt-2 inline-block">
                        {module.duration}
                      </span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      <Dialog open={showVideo} onOpenChange={setShowVideo}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden">
          <div className="relative aspect-video bg-slate-900 flex items-center justify-center">
            <img 
              src="https://images.unsplash.com/photo-1662309376159-b95fb193d96b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHx3b3JrcGxhY2UlMjBzYWZldHklMjB0cmFpbmluZ3xlbnwwfHx8fDE3Njg2OTAyNjJ8MA&ixlib=rb-4.1.0&q=85&w=1200"
              alt="Course preview"
              className="w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white">
                <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-4">
                  <Play className="w-8 h-8 text-white ml-1" />
                </div>
                <h3 className="text-xl font-bold mb-2">Course Introduction Video</h3>
                <p className="text-slate-300">Video preview coming soon!</p>
                <p className="text-sm text-slate-400 mt-2">Sign up to get notified when it&apos;s ready</p>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PreviewSection;
